import { Button } from "@d12labs/ui/button";
import { Input } from "@d12labs/ui/input";
import { Label } from "@d12labs/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@d12labs/ui/select";
import { Textarea } from "@d12labs/ui/textarea";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { ListPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { fitstreakApi } from "../api";
import { announceBadges } from "../components/announce-badges";
import { FormShell, WhenField } from "../components/form-shell";
import { ACTIVITY_TYPE_LABELS } from "../format";

// The cardio/generic types loggable here (strength & mobility have their own pages).
const CARDIO_TYPES = ["run", "ride", "swim", "hike", "other"] as const;
type CardioType = (typeof CARDIO_TYPES)[number];

export function LogActivityPage() {
	const navigate = useNavigate();
	const qc = useQueryClient();
	const [type, setType] = useState<CardioType>("run");
	const [title, setTitle] = useState("");
	const [when, setWhen] = useState("");
	const [durationMin, setDurationMin] = useState("");
	const [distanceKm, setDistanceKm] = useState("");
	const [elevationM, setElevationM] = useState("");
	const [avgHr, setAvgHr] = useState("");
	const [calories, setCalories] = useState("");
	const [notes, setNotes] = useState("");

	const intOrUndef = (v: string) => (v.trim() === "" ? undefined : Math.round(Number(v)));

	const save = useMutation({
		mutationFn: () =>
			fitstreakApi.createActivity({
				input: {
					type,
					title: title || undefined,
					startTime: when || undefined,
					notes: notes || undefined,
					durationSec: durationMin ? Math.round(Number(durationMin) * 60) : undefined,
					distanceM: distanceKm ? Math.round(Number(distanceKm) * 1000) : undefined,
					elevationM: intOrUndef(elevationM),
					avgHr: intOrUndef(avgHr),
					calories: intOrUndef(calories),
				},
			}),
		onSuccess: (res) => {
			toast.success("Activity logged");
			announceBadges(res.newBadges);
			qc.invalidateQueries({ queryKey: ["fitstreak"] });
			navigate({ to: "/fitstreak" });
		},
		meta: { errorMessage: "Failed to log activity" },
	});

	return (
		<FormShell
			icon={ListPlus}
			title="Log activity"
			subtitle="For sessions you do outside a synced device. Garmin/TrainingPeaks fill these in automatically once connected."
			actions={
				<>
					<Button variant="ghost" size="sm" onClick={() => navigate({ to: "/fitstreak" })}>
						Cancel
					</Button>
					<Button size="sm" disabled={save.isPending} onClick={() => save.mutate()}>
						Log activity
					</Button>
				</>
			}
		>
			<div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
				<div className="flex flex-col gap-1.5">
					<Label htmlFor="type">Type</Label>
					<Select value={type} onValueChange={(v) => setType(v as CardioType)}>
						<SelectTrigger id="type" className="w-full">
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{CARDIO_TYPES.map((t) => (
								<SelectItem key={t} value={t}>
									{ACTIVITY_TYPE_LABELS[t]}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
				<div className="flex flex-col gap-1.5">
					<Label htmlFor="title">Title (optional)</Label>
					<Input
						id="title"
						placeholder="e.g. Lakefront long run"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
					/>
				</div>
			</div>

			<WhenField value={when} onChange={setWhen} />

			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
				<Field label="Duration (min)" value={durationMin} onChange={setDurationMin} />
				<Field label="Distance (km)" value={distanceKm} onChange={setDistanceKm} step="0.1" />
				<Field label="Elevation (m)" value={elevationM} onChange={setElevationM} />
				<Field label="Avg HR (bpm)" value={avgHr} onChange={setAvgHr} />
				<Field label="Calories" value={calories} onChange={setCalories} />
			</div>

			<div className="flex flex-col gap-1.5">
				<Label htmlFor="notes">Notes (optional)</Label>
				<Textarea id="notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
			</div>
		</FormShell>
	);
}

function Field({
	label,
	value,
	onChange,
	step,
}: {
	label: string;
	value: string;
	onChange: (v: string) => void;
	step?: string;
}) {
	return (
		<div className="flex flex-col gap-1.5">
			<Label>{label}</Label>
			<Input
				type="number"
				min={0}
				step={step}
				value={value}
				onChange={(e) => onChange(e.target.value)}
			/>
		</div>
	);
}
